import { ActionTree } from "vuex";
import { IStoreRootState } from "@/store/interface";
import { getDraftFilter } from "@/feature/Forex/store/draft/utils";
import { ForexApprovalState } from "@/feature/Forex/store/approval/interface";
import {
	ForexApprovalAction,
	ForexApprovalGetter,
	ForexApprovalMutation
} from "@/feature/Forex/store/approval/constants";
import { getApprovalNextPageFilters } from "@/feature/Forex/store/approval/utils";
import {
	$deleteForeignExchange,
	$getForexListToApprove,
	$getForexToApproveCount,
	$getSwiftPaymentsToApproveCount,
	$getUzsPaymentsToApproveCount
} from "@/feature/Forex/api/approval/forex-approval";
import { nextPageIsAvailable } from "@/feature/Forex/store/utils";
import { PaymentOrderCount, PaymentOrderType } from "@/feature/Forex/api/approval/forex-approval.interface";
import { $getPayrollList } from "@/api/payroll";
import { ForexStatus } from "@/feature/Forex/api/history/forex-history.interface";
import { profileEntity } from "@/models/modules/profile/entity";
import { UserRole } from "@/mixins/role/interface";

export default <ActionTree<ForexApprovalState, IStoreRootState>> {

	async [ForexApprovalAction.loadNextPage](context) {
		if (!nextPageIsAvailable(context, ForexApprovalGetter.loading))
			return [];

		return context.dispatch(
			ForexApprovalAction.loadPage,
			getApprovalNextPageFilters(context.state, context.commit)
		);
	},

	async [ForexApprovalAction.loadPage]({ state, commit }) {
		commit(ForexApprovalMutation.setLoading, true);

		const list = await $getForexListToApprove(getDraftFilter(state as any));

		commit(ForexApprovalMutation.addList, list);
		commit(ForexApprovalMutation.setLoading, false);

		if (list.length < state.pageSize)
			commit(ForexApprovalMutation.setIsEndPage, true);

		return list;
	},

	async [ForexApprovalAction.reloadPage]({ state, commit, dispatch }) {
		commit(ForexApprovalMutation.resetPage);

		return dispatch(ForexApprovalAction.loadPage, getDraftFilter(state as any));
	},

	async [ForexApprovalAction.deleteItems]({ commit }, ids: number[]) {
		if (!ids?.length) return false;

		const deleted = await $deleteForeignExchange(ids);

		deleted && commit(ForexApprovalMutation.deleteItems, ids);

		return deleted;
	},

	async [ForexApprovalAction.loadPaymentOrderCount]({ commit, state }) {
		const [
			uzsCount,
			swiftCount,
			payrollCount,
		]: any = await Promise.all([
			$getUzsPaymentsToApproveCount(),
			$getSwiftPaymentsToApproveCount(),
			profileEntity?.profileData?.role === UserRole.DIRECTOR
				? $getPayrollList(ForexStatus.ON_DIRECTOR_LIST)
				: Promise.resolve([]),
		]);

		commit(ForexApprovalMutation.setPaymentOrderCount, {
			[PaymentOrderType.UZS]: (uzsCount || 0) + (payrollCount.length || 0),
			[PaymentOrderType.SWIFT]: swiftCount,
		} as PaymentOrderCount);

		return state.paymentOrderCount;
	},

	async [ForexApprovalAction.loadToApprovalCount]({ commit }) {
		const count = await $getForexToApproveCount();

		commit(ForexApprovalMutation.setToApprovalCount, count);

		return count;
	},

	async [ForexApprovalAction.loadCount]({ dispatch, state }) {
		await Promise.all([
			dispatch(ForexApprovalAction.loadPaymentOrderCount),
			dispatch(ForexApprovalAction.loadToApprovalCount),
		]);

		return state;
	}

}